import React, { useEffect, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import SlideBase from './SlideBase'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import BodyText from 'LuxKit/Typography/BodyText'
import useVideoBackgroundWithFallback from '../../hooks/useVideoBackgroundWithFallback'

// Get slide config for this slide (static, doesn't depend on runtime data)
const SLIDE_CONFIG = WRAPPED_SLIDES.find(s => s.id === 'nights')
const SLIDE_ID = 'nights' as const

function NightsSpentSlide() {
  const intl = useIntl()
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)

  // Use the video background with fallback hook
  const { background, isBackgroundReady, isCurrentSlide } = useVideoBackgroundWithFallback({
    slideConfig: SLIDE_CONFIG,
    slideId: SLIDE_ID,
  })

  const [animate, setAnimate] = useState(!SLIDE_CONFIG?.video) // If no video, animate immediately

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  // Start animation when background is ready (video loaded or fallback image)
  useEffect(() => {
    if (SLIDE_CONFIG?.video && isBackgroundReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isBackgroundReady, isCurrentSlide])

  // Need either user data or totals data to render
  if (!data && !totalsData) return null

  const userNights = data?.stats.totalNights ?? 0
  const useTotalsFallback = isTotalsOnlyMode || userNights <= 0
  const totalNights = totalsData?.stats.totalNights ?? 0

  // Extract ternary title variants as constants
  const titleTotalsFallback = intl.formatMessage({
    id: '7d4uqwYuy0Nzb4J0R-MI',
    defaultMessage: 'In 2025, Luxury Escapes travellers spent a total of',
    description: 'Nights spent slide heading for totals-only mode in the year-in-review feature',
  })
  const titleUser = intl.formatMessage({
    id: 'tQVmGiRaVW7KGrayQrXF',
    defaultMessage: 'Last year, you spent a total of',
    description: 'Nights spent slide heading for user mode in the wrapped summary',
  })
  const title = useTotalsFallback ? titleTotalsFallback : titleUser

  const captionText = intl.formatMessage({
    id: 'JA2EuqfrIpeI9-GA3ohu',
    defaultMessage: 'Total Nights Spent',
    description: 'Caption for the total nights spent statistic slide in the year-end wrapped summary',
  })
  const bodyTextTotals = intl.formatMessage({
    id: '5U9oqB32P70EQK2pE7rm',
    defaultMessage: 'That’s 4,300 years of travel – enough to check in before the Pyramids, Colosseum and Great Wall of China.',
    description: 'Nights spent slide body text for totals-only mode in the year-in-review feature',
  })

  const nightsDisplayed = useTotalsFallback ? totalNights : userNights
  const useCompactFormat = nightsDisplayed >= 1_000_000

  return (
    <SlideBase background={background}>
      {animate &&
        <FadeBackground>
          <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">{captionText}</Caption>
              <Heading variant="heading5"><b>{title}</b></Heading>
            </Group>
            <Heading variant="heading1">
              <i>
                <FormattedMessage
                  id="4w4bUnLkzvm-YEO-iemN"
                  defaultMessage="{formatDecimal} nights away"
                  description="Nights spent slide nights count with FormatDecimal component"
                  values={{
                    formatDecimal: <FormatDecimal value={nightsDisplayed} notation={useCompactFormat ? 'compact' : 'standard'} compactDisplay="long" maximumFractionDigits={1} />,
                  }}
                />
              </i>
            </Heading>
            {useTotalsFallback && <BodyText variant="medium">
              {bodyTextTotals}
            </BodyText>}
          </Group>
        </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(NightsSpentSlide)
